import client from './client';
import type { Product, ProductDraft, ProductDraftPayload } from '@/types/api';

type RawProductDraft = Omit<ProductDraft, 'payload'> & { payload: string | ProductDraftPayload };

function parsePayload(payload: string | ProductDraftPayload): ProductDraftPayload {
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload) as ProductDraftPayload;
    } catch {
      return {};
    }
  }
  return payload ?? {};
}

function normalizeDraft(raw: RawProductDraft): ProductDraft {
  return {
    ...raw,
    payload: parsePayload(raw.payload),
  };
}

function serializeDraftBody(data: {
  payload: ProductDraftPayload;
  currentStep?: string;
  formMode?: 'wizard' | 'single';
  label?: string;
}) {
  return {
    payload: JSON.stringify(data.payload),
    currentStep: data.currentStep,
    formMode: data.formMode,
    label: data.label,
  };
}

export const productDraftsApi = {
  getAll: async (): Promise<ProductDraft[]> => {
    const response = await client.get<RawProductDraft[]>('/product-drafts');
    return response.data.map(normalizeDraft);
  },

  getMine: async (): Promise<ProductDraft[]> => {
    const drafts = await productDraftsApi.getAll();
    return drafts.filter((draft) => !draft.publishedProductId);
  },

  getById: async (id: string): Promise<ProductDraft> => {
    const response = await client.get<RawProductDraft>(`/product-drafts/${id}`);
    return normalizeDraft(response.data);
  },

  create: async (data: {
    payload: ProductDraftPayload;
    currentStep?: string;
    formMode?: 'wizard' | 'single';
    label?: string;
  }): Promise<ProductDraft> => {
    const response = await client.post<RawProductDraft>('/product-drafts', serializeDraftBody(data));
    return normalizeDraft(response.data);
  },

  update: async (
    id: string,
    data: {
      payload?: ProductDraftPayload;
      currentStep?: string;
      formMode?: 'wizard' | 'single';
      label?: string;
    }
  ): Promise<ProductDraft> => {
    const body: Record<string, string | undefined> = {};
    if (data.payload !== undefined) body.payload = JSON.stringify(data.payload);
    if (data.currentStep !== undefined) body.currentStep = data.currentStep;
    if (data.formMode !== undefined) body.formMode = data.formMode;
    if (data.label !== undefined) body.label = data.label;
    const response = await client.put<RawProductDraft>(`/product-drafts/${id}`, body);
    return normalizeDraft(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await client.delete(`/product-drafts/${id}`);
  },

  publish: async (id: string): Promise<Product> => {
    const response = await client.post<Product>(`/product-drafts/${id}/publish`);
    return response.data;
  },
};
